import { PopoverProps, SxProps, Theme } from '@mui/material';
import { is } from './dom.guards';
import { KeyboardKeys } from '@shared/models/keyboard.model';
import { guards } from '@shared/helpers/typeguards.helpers';

type SxInput = SxProps<Theme> | undefined | false | null;

/**
 * Combines {@link SxProps} values into one list MUI v9 accepts.
 * Drops `undefined`, `null`, and `false`, and flattens nested arrays so optional
 * `sx` can be passed without leaving holes in the array.
 */
export const mergeSx = (...parts: SxInput[]): SxProps<Theme> => {
  const flattened: unknown[] = [];
  for (const part of parts) {
    if (part == null || part === false) continue;
    // Nested arrays come from composing existing `sx` props with extra styles.
    if (guards.is.array(part)) {
      for (const item of part) {
        if (item == null || item === false) continue;
        flattened.push(item);
      }
      continue;
    }
    flattened.push(part);
  }
  return flattened as SxProps<Theme>;
};

type MuiSlot<TProps, TOwnerState> =
  TProps | ((ownerState: TOwnerState) => TProps);

const isSlotCallback = <TProps, TOwnerState>(
  slot: MuiSlot<TProps, TOwnerState>,
): slot is (ownerState: TOwnerState) => TProps => guards.is.function(slot);

/**
 * Resolves an MUI slot prop that may be a plain object or an ownerState callback.
 */
export const resolveMuiSlotProps = <TProps, TOwnerState>(
  slot: MuiSlot<TProps, TOwnerState> | undefined,
  ownerState: TOwnerState,
): TProps | undefined => {
  if (!guards.is.defined(slot)) return;
  if (isSlotCallback(slot)) return slot(ownerState);
  return slot;
};

type PopoverOnClose = NonNullable<PopoverProps['onClose']>;
export type PopoverCloseHandler = PopoverOnClose & (() => void);

/**
 * Handles popover close events. Makes sure escape doesn't close the popover if it's an input or textarea.
 * @param closeHandler the handler to call when the popover should be closed.
 * @returns
 */
export const createPopoverCloseHandler = (
  closeHandler: () => void,
): PopoverCloseHandler => {
  return ((event) => {
    const target = event && 'target' in event ? event.target : undefined;
    const key = event && 'key' in event ? event.key : undefined;

    if (target && key === KeyboardKeys.ESCAPE) {
      if (is.Input(target) || is.TextArea(target)) {
        return;
      }
    }

    closeHandler();
  }) as PopoverCloseHandler;
};
